// Modal with title, detail copy, and actions (up to 3, at least 1)
import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { type InlineEditorHints } from '@bbk/ui/inline-editing';
import {
  type RestModalForwards,
  type RestModalMultiCTATypeII,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { type ReactFCC } from '@bbkAdminUtils/utility-types';
import CustomerHtml from '@bbkFunnelComponents/customer_html/customer_html';
import {
  ModalDesktopInnerSpacer,
  ModalMobileInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '@bbkFunnelComponents/modals/sections';
import { MultiCTAOtherActions } from '@bbkFunnelComponents/modals/v2/multi-cta-actions';

import './modal.scss';

type Props = {
  modalInfo: RestModalMultiCTATypeII;
  close: () => void;
  handleAction: (action: RestModalForwards) => void;
};

const CopyContent = styled.div`
  overflow-y: auto;
  padding-bottom: 10px;
`;

const RenderMultiCTATypeII: ReactFCC<Props> = (props) => {
  const {
    modalInfo: {
      id,
      requires: {
        info: { title, details, image },
        generic_actions = [],
      },
    },
    close,
    handleAction,
  } = props;
  const { mobileView } = useMediaQueries();

  const header = <TitleTheme data-part="title" title={title} />;
  const copy = <CustomerHtml value={details} />;

  const multiCTAOtherActions = (
    <div
      className="display-grid action-items"
      css={css`
        gap: 1rem;
      `}
    >
      <MultiCTAOtherActions
        actions={generic_actions}
        handleAction={handleAction}
      />
    </div>
  );
  if (mobileView) {
    return (
      <ModalMobileInnerSpacer
        className="display-grid multi-cta-type-II mobile inner-spacer"
        css={css`
          overflow-y: auto;
        `}
      >
        <OfferTitleMobile title={header} close={close} />
        <img
          data-part="image"
          alt=""
          src={image}
          css={css`
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
            border-radius: 4px;
          `}
        />
        <CopyContent
          data-part="description"
          css={css`
            padding-top: 10px;
          `}
        >
          {copy}
        </CopyContent>
        {multiCTAOtherActions}
      </ModalMobileInnerSpacer>
    );
  }

  return (
    <ModalDesktopInnerSpacer
      css={css`
        padding: 0;
        max-width: 900px;
      `}
      className="multi-cta-type-II display-grid desktop inner-spacer"
    >
      <img
        data-part="image"
        alt=""
        src={image}
        css={css`
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        `}
      />
      <div
        css={css`
          padding: 1rem 1.5rem 2rem 2rem;
        `}
        className="display-grid content-grid"
      >
        <OfferTitleMobile title={<TitleTheme title={header} />} close={close} />
        <CopyContent data-part="description">{copy}</CopyContent>
        {multiCTAOtherActions}
      </div>
    </ModalDesktopInnerSpacer>
  );
};

export const MultiCTATypeII = Object.assign(RenderMultiCTATypeII, {
  editorHints: {
    title: {
      path: ['requires', 'info', 'title'],
      control: 'richText',
      selector: '[data-part="title"]',
      placement: 'right',
      options: {
        formatting: false,
      },
    },
    description: {
      path: ['requires', 'info', 'details'],
      control: 'richText',
      selector: '[data-part="description"]',
      placement: 'right',
    },
    image: {
      path: ['requires', 'info', 'image'],
      control: 'image',
      selector: '[data-part="image"]',
      placement: 'top-end',
      options: {
        helpLabel: 'Recommended size: 550x700px; < 250kB',
      },
    },
    actions: {
      path: ['requires', 'generic_actions'],
      control: 'ctaAppearance',
      selector: '[data-part="cta"]',
      placement: 'top-end',
      label: 'Action',
      indexAttribute: 'data-action-index',
    },
  } satisfies InlineEditorHints,
});
